<template>
  <v-card class="mt-6" id="casper">
    <v-card-title>
      <a href="https://casper.ast.cam.ac.uk" target="_blank" rel="noopener" class="casper-title-link">CASPER</a>
      <v-spacer></v-spacer>
      <v-btn small color="primary" @click="dialog = true"><v-icon left small>mdi-key-plus</v-icon> New token</v-btn>
    </v-card-title>
    <v-card-text>
      <p class="mb-2">
        <a href="https://casper.ast.cam.ac.uk" target="_blank" rel="noopener">CASPER</a> is the CamCEAD assistant: it answers questions from CamCEAD's own
        documents and can use tools for you.
        <a href="https://camcead.ast.cam.ac.uk/project_casper" target="_blank" rel="noopener">What is CASPER?</a>
      </p>
      <p class="mb-2">
        CASPER can work on CAMCEADHUB as you: your servers, storage and profile, with your own permissions and under your own name. It can never become another person,
        fetch a kubeconfig, or change your password, two-factor set-up or tokens.
      </p>
      <v-switch
        v-model="allowAssertion"
        :disabled="!crsid || !assertionsConfigured"
        :label="crsid ? 'Allow CASPER to act as me when I am signed in to CASPER with Raven (CRSid ' + crsid + ')' : 'Add your CRSid to your profile to allow CASPER to act as you through Raven'"
        inset
        dense
        @change="toggle"
      ></v-switch>
      <p class="text-caption mb-3">
        Otherwise connect from CASPER's own profile page
        (<a href="https://casper.ast.cam.ac.uk/profile#connected" target="_blank" rel="noopener">Connected accounts &rarr; CAMCEADHUB &rarr; Connect</a>),
        or create a token here and paste it there. Tokens are valid for at most one year and can be revoked below at any time.
      </p>
      <v-alert v-if="created" type="success" text dense>
        Token <strong>{{ created.name }}</strong> created. Copy it now, it is shown only once:
        <code class="d-block mt-1" style="word-break: break-all">{{ created.token }}</code>
      </v-alert>
      <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>
      <v-simple-table v-if="tokens.length > 0" dense>
        <thead>
          <tr><th>Name</th><th>Starts with</th><th>Scope</th><th>Made by</th><th>Expires</th><th>Last used</th><th>Uses</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="t in tokens" :key="t.id" :class="{ 'text--disabled': !t.active }">
            <td>{{ t.name }}</td>
            <td><code>{{ t.prefix }}…</code></td>
            <td>{{ t.scopes }}</td>
            <td>{{ t.client === 'casper' ? 'CASPER (Connect)' : 'me' }}</td>
            <td>{{ t.expires.substring(0, 10) }}</td>
            <td>{{ t.last_used ? t.last_used.substring(0, 16).replace('T', ' ') : '—' }}</td>
            <td>{{ t.use_count }}</td>
            <td>
              <v-btn v-if="t.active" x-small outlined color="error" @click="revoke(t)">revoke</v-btn>
              <span v-else class="text-caption">{{ t.revoked ? 'revoked' : 'expired' }}</span>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <p v-else class="text-caption mb-0">No tokens.</p>
    </v-card-text>

    <v-dialog v-model="dialog" max-width="480">
      <v-card>
        <v-card-title>New CASPER token</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" label="Name (e.g. CASPER laptop)" dense outlined maxlength="60"></v-text-field>
          <v-select v-model="form.days" :items="validity" label="Valid for" dense outlined></v-select>
          <v-select v-model="form.scopes" :items="[{ text: 'Full (what I can do)', value: 'full' }, { text: 'Read only', value: 'read' }]" label="Scope" dense outlined></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="busy" @click="create">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
// CASPER bridge card (moved from the dashboard to the profile settings page, 2026-10-01).
import { ACCOUNTS_CASPER_SETTINGS, ACCOUNTS_CASPER_TOKENS, API } from "@/axios-config";

export default {
  name: "CasperAccessCard",
  data() {
    return {
      allowAssertion: false,
      crsid: "",
      assertionsConfigured: false,
      tokens: [],
      dialog: false,
      busy: false,
      created: null,
      error: "",
      form: { name: "", days: 90, scopes: "full" },
      validity: [
        { text: "30 days", value: 30 },
        { text: "90 days", value: 90 },
        { text: "180 days", value: 180 },
        { text: "1 year (maximum)", value: 366 },
      ],
    };
  },
  methods: {
    async load() {
      try {
        const { data } = await API.get(ACCOUNTS_CASPER_SETTINGS);
        this.allowAssertion = data.allow_assertion;
        this.crsid = data.crsid;
        this.assertionsConfigured = data.assertions_configured;
        const tokens = await API.get(ACCOUNTS_CASPER_TOKENS);
        this.tokens = tokens.data.tokens;
      } catch (error) {
        console.log(error);
      }
    },
    async toggle(value) {
      this.error = "";
      try {
        await API.post(ACCOUNTS_CASPER_SETTINGS, { allow_assertion: value });
      } catch (error) {
        this.allowAssertion = !value;
        this.error = (error.response && error.response.data && error.response.data.detail) || "Could not save the setting.";
      }
    },
    async create() {
      this.busy = true;
      this.error = "";
      try {
        const { data } = await API.post(ACCOUNTS_CASPER_TOKENS, this.form);
        this.created = data;
        this.dialog = false;
        this.form = { name: "", days: 90, scopes: "full" };
        await this.load();
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.detail) || "Could not create the token.";
      }
      this.busy = false;
    },
    async revoke(t) {
      if (!window.confirm("Revoke the token '" + t.name + "'? Anything using it stops working at once.")) {
        return;
      }
      try {
        await API.delete(ACCOUNTS_CASPER_TOKENS, { data: { id: t.id } });
        if (this.created && this.created.id === t.id) {
          this.created = null;
        }
        await this.load();
      } catch (error) {
        this.error = "Could not revoke the token.";
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.casper-title-link { text-decoration: none; color: inherit; }
.casper-title-link:hover { text-decoration: underline; }
</style>
